import { Component, computed, effect, inject, linkedSignal, resource, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDatepicker } from "@angular/material/datepicker";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgSelectComponent, NgSelectModule } from "@ng-select/ng-select";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { firstValueFrom } from "rxjs";
import { Ticket } from "src/app/_models";
import { ProjectService, TicketService } from "src/app/_services";
import { TicketSearchItem } from "../types";

@Component({
  selector: "ticket-grid-prio-editor",
  template: `
    <ng-select
      class="bm-join-item w-[300px]"
      [items]="options.value()"
      bindLabel="name"
      bindValue="id"
      [ngModel]="selection()"
      (ngModelChange)="selection.set($event)"
      [loading]="options.isLoading()"
      [required]="true"
    >
      <ng-template ng-label-tmp let-item="item">
        <div class="d-flex align-items-center gap-1">
          @if (item.color) {
            <span class="fa-stack" style="font-size: 60%">
              <i class="fa fa-circle fa-stack-2x" style="color: #333"></i>
              <i class="fa fa-circle fa-stack-2x" [style.color]="item.color"></i>
            </span>
          } @else {
            <span>&nbsp;</span>
          }
          <span>{{ item.name }}</span>
        </div>
      </ng-template>
    </ng-select>
    <button
      class="flex-none bm-join-item bm-btn bm-btn-primary bm-btn-square bm-tooltip"
      [attr.data-tip]="'Speichern'"
      (click)="save()"
      [disabled]="isSaving() || isLoading()"
    >
      <fa-icon [icon]="['far', 'save']" />
    </button>
    <button
      class="flex-none bm-join-item bm-btn bm-btn-square bm-tooltip"
      [attr.data-tip]="'Verwerfen'"
      (click)="cancel()"
      [disabled]="isSaving() || isLoading()"
    >
      <fa-icon [icon]="['far', 'times']" />
    </button>
  `,
  imports: [FormsModule, NgSelectModule, FontAwesomeModule],
  host: {
    class: "bm-join",
  },
})
export class TicketGridPrioEditorComponent implements ICellEditorAngularComp {
  protected projectService = inject(ProjectService);
  protected ticketService = inject(TicketService);

  protected params = signal<ICellEditorParams<TicketSearchItem>>(null);
  protected data = computed(() => this.params()?.data);
  protected projectId = computed(() => this.data()?.project_id);
  protected options = resource({
    request: this.projectId,
    loader: async ({ request }) => {
      return firstValueFrom(this.projectService.getPriorities(request));
    },
  });
  protected isSaving = signal(false);
  protected isLoading = this.options.isLoading;

  protected selection = linkedSignal<number>(() => this.data()?.prority_hash?.id);
  protected value = linkedSignal<string>(() => this.params()?.value);
  protected picker = viewChild(NgSelectComponent);

  public agInit(params: ICellEditorParams) {
    this.params.set(params);
  }

  public refresh(params: ICellEditorParams) {
    this.params.set(params);
    return true;
  }

  public getValue() {
    return this.value();
  }

  public afterGuiAttached(): void {
    this.picker().open();
  }

  public isPopup(): boolean {
    return true; // editor doesn't fit into a cell, so it should be a popup
  }

  public async save() {
    this.isSaving.set(true);
    const id = this.data().id;
    const selection = this.options.value().find((it) => it.id === this.selection());
    const update: Pick<Ticket, "priority_id"> = {
      priority_id: this.selection(),
    };
    firstValueFrom(this.ticketService.updateTicket(String(id), update))
      .then((ticket) => {
        // TODO: maybe fetch the updated entry instead of mocking it here
        const { api, data, node } = this.params();
        data.prority_hash = {
          id: ticket.priority_id,
          name: selection.name,
          color: selection.color || null,
        };
        api.refreshCells({ rowNodes: [node] });

        this.value.set(selection.name); // this is written back to the cell
        this.stopEditing();
      })
      .catch(() => {
        // Handle error, e.g., show a notification
        console.error("Failed to update ticket");
      })
      .finally(() => {
        this.isSaving.set(false);
      });
  }

  public cancel() {
    this.params().stopEditing();
  }

  private stopEditing() {
    this.params().stopEditing();
  }
}
